/* ==========================================================================
   NZ Supps — shared "fly to cart"
   Flies the product image into the cart icon whenever a customer adds from a
   PRODUCT CARD or the QUICK-ADD popover, then bounces the icon.

   Scope is deliberate: cards + quick-add only. The PDP and quick-view modal are
   excluded (a generic `button[name="add"]` would sweep them in, so the generic
   token is gated on a card container). The gift popup runs its own multi-source
   staggered flight and is excluded twice over — by selector and by IGNORE_WITHIN.

   No CSS file: the clone and the impact ring are styled inline and animated with
   WAAPI, so this drops in as one asset with nothing else to wire up.
   ========================================================================== */
(function () {
  'use strict';
  if (window.nzsFlyToCart) return;                 // already loaded

  var CART_TOGGLES = ['#nzsCartToggle', '[data-nzs-cart-toggle]', '.js-cart-toggle'];

  /* Containers that make a generic add-button count as a "card" add. */
  var CARD_SEL = '.card-product, .nzs-bs__card, .nzs-qa';
  /* Buttons that are card/quick-add adds regardless of container. */
  var DIRECT_SEL = '.nzs-qa__atc, [data-nzs-qa-add], [data-satc-btn], .nzs-foot-atc, [data-nzs-fly]';
  /* Generic add buttons — only count inside CARD_SEL, which keeps the PDP
     (.product-info) and quick view (#quickview-product-content) out. */
  var GENERIC_SEL = 'button[name="add"], .button--quickview.button-solid';
  /* Never fire inside the gift popup — it animates itself. */
  var IGNORE_WITHIN = '[data-nzs-gp], .nzs-gp';

  var PENDING_TTL = 17000; // NZS-ROUND4B: ADD_DEADLINE is 16s; keep the snapshot through the full retry window.
  var DEDUPE_MS = 250;      // one flight per burst of adds
  var pending = null, pendingTimer = null, lastFlight = 0, current = null;

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function canAnimate() {
    return !reduced() && typeof Element !== 'undefined' && !!Element.prototype.animate;
  }
  function cartTarget() {
    for (var i = 0; i < CART_TOGGLES.length; i++) {
      var el = document.querySelector(CART_TOGGLES[i]);
      if (el && el.getBoundingClientRect().width) return el;
    }
    return null;
  }
  function onScreen(r) {
    return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
  }
  /* Largest <img> in scope. NZS-FLY-450-0903: off-screen images count too (the flight starts from
     the button in that case, see snapshot) so a card add never degrades to the coloured box. */
  function bestImg(scope) {
    if (!scope) return null;
    var imgs = scope.querySelectorAll('img'), best = null, bestArea = 0;
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i], r = im.getBoundingClientRect();
      var a = (r.width * r.height) || (im.naturalWidth * im.naturalHeight) || 0;
      if (a < 576) continue;   // skips badges/cert icons (<24x24)
      if (a > bestArea) { bestArea = a; best = im; }
    }
    return best;
  }

  /* currentSrc is '' on a lazy <img> the browser hasn't picked a candidate for yet; the first
     srcset candidate is already what the card will show, so prefer it over the bare src. */
  function imgUrl(im) {
    var ss = (im.getAttribute('srcset') || '').split(',')[0].trim().split(/\s+/)[0];
    return im.currentSrc || ss || im.src || null;
  }
  /* Best-effort image SRC for any node: an <img>, a container holding one, or a CSS bg-image. */
  function imgSrcOf(el) {
    if (!el) return null;
    if (el.tagName === 'IMG') return imgUrl(el);
    var im = el.querySelector && el.querySelector('img');
    if (im) return imgUrl(im);
    var bg = (el.ownerDocument && el.ownerDocument.defaultView || window).getComputedStyle(el).backgroundImage;
    var m = bg && bg.match(/url\(["']?(.*?)["']?\)/);
    return m ? m[1] : null;
  }

  /* PDP adds (sticky ATC, footer ATC) live outside any card/quick-add wrapper. Resolve the
     product IMAGE so the flight is never the bare button: the sticky bar's own thumbnail first
     (it sits right by the button when the sticky ATC is in use), else the largest image in the
     product media. Returned even if off-screen -- snapshot() keeps the src and starts the flight
     from the button so an off-screen gallery still flies the image, not a coloured box. */
  function findPdpImage() {
    var sticky = document.querySelector('[id^="product-image-sticky-"] img');
    if (sticky && (sticky.currentSrc || sticky.src)) return sticky;
    var media = document.querySelector('.product-media-container, [id^="product-media-"]');
    if (!media) return null;
    var imgs = media.querySelectorAll('img'), best = null, bestArea = 0;
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i]; if (!(im.currentSrc || im.src)) continue;
      var r = im.getBoundingClientRect();
      var a = (r.width * r.height) || (im.naturalWidth * im.naturalHeight) || 1;
      if (a > bestArea) { bestArea = a; best = im; }
    }
    return best;
  }

  /* Resolve the image this add came from. Ladder, most specific first. */
  function findSource(btn) {
    var pick = btn.getAttribute && btn.getAttribute('data-nzs-fly-from');
    if (pick) { var el = document.querySelector(pick); if (el) return el; }
    var qa = btn.closest('.nzs-qa');
    if (qa) { var thumb = qa.querySelector('.nzs-qa__thumb'); if (thumb) return thumb; }
    var card = btn.closest(CARD_SEL);
    if (card) return card.querySelector('img.preview-img') || bestImg(card);
    return findPdpImage();
  }

  /* NZS-FLY-450-0903: the token's <img> is created and decoded at CLICK time, so frame 1 of the
     flight never paints a blank/half-decoded image (the add round-trip is the decode window). */
  function preload(src) {
    if (!src) return null;
    var im = document.createElement('img');
    im.alt = ''; im.decoding = 'async'; im.src = src;
    if (im.decode) im.decode().catch(function () {});
    return im;
  }

  /* Snapshot at CLICK time — by the time the add resolves the quick-add panel is
     gone and the card may have been re-rendered, so we must not hold a node. */
  function snapshot(btn, e) {
    var src = findSource(btn);
    var imgSrc = imgSrcOf(src);
    var r = src && src.getBoundingClientRect();
    /* Image missing or off-screen (e.g. a sticky-ATC add while the gallery is scrolled away):
       still fly the IMAGE -- just start it from the button so it has an on-screen origin. Only
       the genuine no-image case (imgSrc null) falls back to the plain coloured token. */
    if (!r || !r.width || !onScreen(r)) { r = btn.getBoundingClientRect(); }
    return {
      src: imgSrc,
      img: preload(imgSrc),
      rect: r && r.width ? { left: r.left, top: r.top, width: r.width, height: r.height } : null,
      x: (e && e.clientX) || 0,
      y: (e && e.clientY) || 0,
      t: Date.now()
    };
  }

  /* The hit: squash-and-settle bounce + an expanding ring on the cart icon. */
  function hitCart(target) {
    target = target || cartTarget();
    if (!target || !canAnimate()) return;
    target.animate(
      [
        { transform: 'scale(1)', offset: 0 },
        { transform: 'scale(1.34)', offset: .26 },
        { transform: 'scale(.9)', offset: .52 },
        { transform: 'scale(1.12)', offset: .76 },
        { transform: 'scale(1)', offset: 1 }
      ],
      { duration: 480, easing: 'cubic-bezier(.22, 1, .36, 1)' }
    );
    var r = target.getBoundingClientRect();
    var accent = (getComputedStyle(document.documentElement).getPropertyValue('--blue-500') || '').trim() || '#0A5BD6';
    var ring = document.createElement('span');
    ring.className = 'nzs-fly-ring';
    ring.setAttribute('aria-hidden', 'true');
    ring.style.cssText = 'position:fixed;z-index:99999;width:16px;height:16px;margin:-8px 0 0 -8px;'
      + 'border-radius:999px;border:2px solid ' + accent + ';pointer-events:none;'
      + 'left:' + (r.left + r.width / 2) + 'px;top:' + (r.top + r.height / 2) + 'px;';
    document.body.appendChild(ring);
    var a = ring.animate(
      [{ transform: 'scale(.6)', opacity: .9 }, { transform: 'scale(4.4)', opacity: 0 }],
      { duration: 660, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );
    var gone = false, kill = function () { if (gone) return; gone = true; ring.remove(); };
    if (a.finished && a.finished.then) a.finished.then(kill).catch(kill);
    a.onfinish = kill;
    setTimeout(kill, 900);
  }

  /* Fly a token from a snapshot into the cart icon. Always resolves. */
  function fly(source, opts) {
    opts = opts || {};
    var snap = (source && source.nodeType === 1)
      ? { src: imgUrl(source), img: preload(imgUrl(source)), rect: source.getBoundingClientRect() }
      : (source || {});
    var target = opts.target || cartTarget();

    if (!canAnimate()) { hitCart(target); return Promise.resolve(); }

    var r = snap.rect && snap.rect.width ? snap.rect : null;
    if (!r && snap.x) r = { left: snap.x - 22, top: snap.y - 22, width: 44, height: 44 };
    if (!r) { hitCart(target); return Promise.resolve(); }

    /* Header is NOT sticky in this theme, so the cart icon really can be scrolled
       off screen. Aim at where it would be and skip the ring — nothing to ring. */
    var tr = target && target.getBoundingClientRect();
    var offscreen = !tr || !onScreen(tr);
    if (offscreen) tr = { left: window.innerWidth - 56, top: 24, width: 32, height: 32 };

    var size = Math.min(r.width, 120);
    var scale = size / r.width;
    var fx = document.createElement('div');
    fx.className = 'nzs-fly';
    fx.setAttribute('aria-hidden', 'true');
    fx.style.cssText = 'position:fixed;z-index:100000;pointer-events:none;overflow:hidden;will-change:transform,opacity;'
      + 'border-radius:8px;background:#fff;box-shadow:0 12px 28px rgba(10,14,21,.14);'
      + 'left:' + r.left + 'px;top:' + r.top + 'px;'
      + 'width:' + size + 'px;height:' + (r.height * scale) + 'px;';
    if (snap.src) {
      var im = snap.img || preload(snap.src);
      im.style.cssText = 'width:100%;height:100%;object-fit:contain;padding:4px;box-sizing:border-box';
      fx.appendChild(im);
    } else {
      fx.style.background = (getComputedStyle(document.documentElement).getPropertyValue('--blue-500') || '#0A5BD6').trim();
    }
    document.body.appendChild(fx);

    var fr = fx.getBoundingClientRect();
    var dx = (tr.left + tr.width / 2) - (fr.left + fr.width / 2);
    var dy = (tr.top + tr.height / 2) - (fr.top + fr.height / 2);
    var arc = Math.min(110, Math.abs(dy) * .3 + 50);
    var delay = opts.delay || 0;
    var dur = opts.duration || 450;   // NZS-FLY-450-0903 (Blake): was 780
    var impact = .9;

    var anim = fx.animate(
      [
        { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
        { transform: 'translate(' + (dx * .5) + 'px,' + (dy * .5 - arc) + 'px) scale(.66) rotate(7deg)', opacity: 1, offset: .55 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.26) rotate(12deg)', opacity: 1, offset: impact },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.08) rotate(14deg)', opacity: 0, offset: 1 }
      ],
      { duration: dur, delay: delay, easing: 'cubic-bezier(.4,.02,.32,1)', fill: 'forwards' }
    );

    // bounce on contact, not when the tween ends
    if (!offscreen) setTimeout(function () { hitCart(target); }, delay + dur * impact);

    var p = new Promise(function (resolve) {
      var landed = false;
      var land = function () { if (landed) return; landed = true; fx.remove(); if (current === p) current = null; resolve(); };
      if (anim.finished && anim.finished.then) anim.finished.then(land).catch(land);
      anim.onfinish = land;
      setTimeout(land, delay + dur + 160);        // safety net for a throttled tab
    });
    current = p;
    return p;
  }

  /* ------------------------- origin capture ------------------------- */
  function arm(snap) {
    pending = snap;
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () { pending = null; }, PENDING_TTL);
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest(IGNORE_WITHIN)) return;                       // gift popup self-animates
    var btn = t.closest(DIRECT_SEL) || (t.closest(GENERIC_SEL) && t.closest(CARD_SEL) ? t.closest(GENERIC_SEL) : null);
    if (!btn || btn.disabled) return;
    if (/choose option/i.test(btn.textContent || '')) return;   // opens a picker, not an add
    arm(snapshot(btn, e));
  }, true);

  function onAddSuccess() {
    var p = pending;
    pending = null;
    clearTimeout(pendingTimer);
    if (!p || !window.nzsFlyToCart.enabled) return;
    if (Date.now() - p.t > PENDING_TTL) return;
    if (Date.now() - lastFlight < DEDUPE_MS) return;
    lastFlight = Date.now();
    fly(p);
  }

  /* Success signal — mirrors the header's own /cart/add detector. Guarded so a
     double include can't wrap fetch twice. */
  if (!window.__nzsFlyHooked) {
    window.__nzsFlyHooked = true;
    function isCartAdd(input, init) {
      try {
        var req = typeof Request !== 'undefined' && input instanceof Request;
        var url = new URL(req ? input.url : input, location.href);
        var method = String(init && init.method != null ? init.method : (req ? input.method : 'GET')).toUpperCase();
        var route = window.Shopify && Shopify.routes && Shopify.routes.cart_add_url;
        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        var addPath = new URL(route || root + 'cart/add', location.href).pathname.replace(/\.js$/, '');
        return url.origin === location.origin && method === 'POST' &&
          (url.pathname === addPath || url.pathname === addPath + '.js');
      } catch (e) { return false; }
    }
    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (input, init) {
        var isAdd = isCartAdd(input, init);
        return origFetch.apply(this, arguments).then(function (r) {
          if (isAdd && r.ok) onAddSuccess();     // r.ok is false on 422 sold-out
          return r;
        });
      };
    }
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (m, u) {
      this.__nzsFlyAdd = isCartAdd(u, { method: m });
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__nzsFlyAdd) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) onAddSuccess();
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  window.nzsFlyToCart = fly;
  window.nzsFlyToCart.hitCart = hitCart;
  window.nzsFlyToCart.arm = function (el) { if (el) arm(snapshot(el, null)); };
  window.nzsFlyToCart.target = cartTarget;
  window.nzsFlyToCart.reduced = reduced;
  window.nzsFlyToCart.enabled = true;
  /* NZS-FLY-450-0903: run fn when the in-flight token lands - or now if nothing is flying (no
     module use, reduced motion, cart icon missing). Replaces the fixed 900ms drawer delays. */
  window.nzsFlyToCart.after = function (fn) { if (current) current.then(fn, fn); else fn(); };
  // ponytail: consumers need the live API, not an unread version marker.
})();
